import React, { useRef, useImperativeHandle, forwardRef, useState, useEffect } from 'react';
import { LiveAnswer } from './LiveAnswers';
import { Standings } from './Standings';
import { TeamStandings, buildTeamStandingRows } from './TeamStandings';
import { TeamsState } from '../../types/teams';

interface Player {
  id: string;
  name: string;
  score: number;
  avatar?: string;
  teamId?: 'A' | 'B';
}

export interface StandingsSidebarRef {
  getScoreElement: (playerId: string) => HTMLElement | null;
}

interface StandingsSidebarProps {
  /** Array of players to display in standings */
  players: Player[];
  /** Live answers to display */
  liveAnswers: LiveAnswer[];
  /** Total number of players */
  totalPlayers: number;
  /** Current question ID - when this changes, scores are frozen. When it stays same but scores change, scores update. */
  questionId?: string;
  /** Whether scores should be updated (set to true when question-result is received) */
  shouldUpdateScores?: boolean;
  /** Whether this is mobile view */
  isMobile?: boolean;
  /** Theme colors */
  theme?: {
    primary?: string;
    primaryLight?: string;
    primaryBorder?: string;
    accent?: string;
    accentLight?: string;
    accentBorder?: string;
  };
  /** Maximum number of players to show in standings (default: 6 for desktop, 4 for mobile) */
  maxPlayers?: number;
  /** When set (e.g. from bonus-level-result), use these points for green +N instead of score diff */
  pointsGainedOverride?: Map<string, number>;
  /** Visual style variant for the sidebar */
  variant?: 'default' | 'light-card';
  /** players (default) | teams — Team vs Team games */
  mode?: 'players' | 'teams';
  /** Room teams meta (scores/names) when mode=teams */
  teams?: TeamsState;
}

/** How long to show live answers before switching to green +points. */
const LIVE_ANSWERS_DURATION_MS = 500;
/** Total duration for green +points display (including live-answers phase). */
const GREEN_POINTS_TOTAL_MS = 5000;

/**
 * Reusable sidebar component that displays:
 * - 🏆 Standings
 * - Live answers (who answered what in real-time)
 * 
 * Responsive: horizontal layout on mobile, vertical on desktop
 * 
 * Players smoothly animate to new positions when scores change and they're re-sorted.
 */
export const StandingsSidebar = forwardRef<StandingsSidebarRef, StandingsSidebarProps>(({
  players,
  liveAnswers,
  totalPlayers,
  questionId,
  shouldUpdateScores = false,
  isMobile = false,
  theme = {},
  maxPlayers,
  pointsGainedOverride,
  variant = 'default',
  mode = 'players',
  teams,
}, ref) => {
  const accent = theme.accent || '#F59E0B';
  const scoreRefs = useRef<Map<string, HTMLSpanElement>>(new Map());

  // When true, show given-answer column (live answers); set false when everyone has answered so we show only green +points
  const [showGivenAnswers, setShowGivenAnswers] = useState(true);
  // Track points gained for animation (green +N pts)
  const [pointsGained, setPointsGained] = useState<Map<string, number>>(new Map());
  const [showPointsGained, setShowPointsGained] = useState<Map<string, boolean>>(new Map());
  const previousPlayersRef = useRef<Player[]>(players);
  const previousTeamsRef = useRef<TeamsState | undefined>(teams);
  const previousQuestionIdRef = useRef<string | undefined>(questionId);
  const previousShouldUpdateScoresRef = useRef<boolean>(false);
  /** Sum of positive points we last displayed (for bonus-points-updated re-trigger) */
  const lastDisplayedPositiveSumRef = useRef<number>(0);

  // Expose refs to parent (keeping for backward compatibility)
  useImperativeHandle(ref, () => ({
    getScoreElement: (playerId: string) => {
      return scoreRefs.current.get(playerId) || null;
    },
  }));

  // When question changes, reset and show given-answer column again.
  // When syncing previousPlayersRef, never overwrite with lower scores (avoids stale room-state
  // on prod where room-state can arrive after question-result, so "new question" had stale players).
  useEffect(() => {
    if (questionId && questionId !== previousQuestionIdRef.current) {
      previousQuestionIdRef.current = questionId;
      lastDisplayedPositiveSumRef.current = 0;
      const prev = previousPlayersRef.current;
      previousPlayersRef.current = players.map((p) => {
        const existing = prev.find((e) => e.id === p.id);
        if (!existing) return p;
        const score = Math.max(existing.score, p.score);
        return { ...p, score };
      });
      if (teams) {
        const prevTeams = previousTeamsRef.current;
        previousTeamsRef.current = {
          A: {
            ...teams.A,
            score: Math.max(prevTeams?.A.score ?? 0, teams.A.score),
          },
          B: {
            ...teams.B,
            score: Math.max(prevTeams?.B.score ?? 0, teams.B.score),
          },
        };
      }
      setPointsGained(new Map());
      setShowPointsGained(new Map());
      setShowGivenAnswers(true);
      previousShouldUpdateScoresRef.current = false;
    }
  }, [questionId, players, teams]);

  // When everyone has answered: keep live answers briefly, then hide and show green +points.
  // Also re-trigger when bonus-points-updated brings better data (e.g. late fast-fingers submits).
  useEffect(() => {
    const positiveSum = pointsGainedOverride
      ? Array.from(pointsGainedOverride.values()).reduce((s, p) => s + (p > 0 ? p : 0), 0)
      : 0;
    const hasBetterData = positiveSum > lastDisplayedPositiveSumRef.current;
    const teamScoreIncreased =
      mode === 'teams' &&
      !!teams &&
      !!previousTeamsRef.current &&
      (['A', 'B'] as const).some(
        (teamId) => teams[teamId].score > (previousTeamsRef.current?.[teamId].score ?? 0)
      );
    const shouldTrigger =
      shouldUpdateScores &&
      (!previousShouldUpdateScoresRef.current || hasBetterData || teamScoreIncreased);

    if (shouldTrigger) {
      previousShouldUpdateScoresRef.current = true;
      lastDisplayedPositiveSumRef.current = positiveSum;

      const newPointsGained = new Map<string, number>();
      const newShowPointsGained = new Map<string, boolean>();

      if (pointsGainedOverride && pointsGainedOverride.size > 0) {
        pointsGainedOverride.forEach((points, playerId) => {
          if (points > 0) {
            newPointsGained.set(playerId, points);
            newShowPointsGained.set(playerId, true);
          }
        });
      } else if (mode === 'teams' && teams) {
        const prevTeams = previousTeamsRef.current;
        if (prevTeams) {
          (['A', 'B'] as const).forEach((teamId) => {
            const gained = teams[teamId].score - prevTeams[teamId].score;
            if (gained > 0) {
              newPointsGained.set(teamId, gained);
              newShowPointsGained.set(teamId, true);
            }
          });
        }
        previousTeamsRef.current = {
          A: { ...teams.A },
          B: { ...teams.B },
        };
      } else {
        players.forEach((player) => {
          const previousPlayer = previousPlayersRef.current.find(p => p.id === player.id);
          if (previousPlayer && player.score > previousPlayer.score) {
            const points = player.score - previousPlayer.score;
            newPointsGained.set(player.id, points);
            newShowPointsGained.set(player.id, true);
          }
        });
      }

      setTimeout(() => {
        setShowGivenAnswers(false);
        setPointsGained(newPointsGained);
        setShowPointsGained(newShowPointsGained);
      }, LIVE_ANSWERS_DURATION_MS);

      setTimeout(() => {
        setShowPointsGained(new Map());
        setPointsGained(new Map());
      }, GREEN_POINTS_TOTAL_MS);

      previousPlayersRef.current = players;
    } else if (!shouldUpdateScores) {
      previousShouldUpdateScoresRef.current = false;
      lastDisplayedPositiveSumRef.current = 0;
    }
  }, [players, shouldUpdateScores, pointsGainedOverride, mode, teams]);

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: isMobile ? 'row' : 'column', 
      gap: '1rem',
      ...(isMobile ? { marginBottom: '1rem' } : {}),
    }}>
      {mode === 'teams' ? (
        <TeamStandings
          teams={buildTeamStandingRows(players, teams)}
          isMobile={isMobile}
          accent={accent}
          variant={variant}
          pointsGained={pointsGained}
          showPointsGained={showPointsGained}
        />
      ) : (
        <Standings
          players={players}
          isMobile={isMobile}
          accent={accent}
          variant={variant}
          maxPlayers={maxPlayers}
          shouldUpdateScores={shouldUpdateScores}
          questionId={questionId}
          pointsGained={pointsGained}
          showPointsGained={showPointsGained}
          liveAnswers={liveAnswers}
          totalPlayers={totalPlayers}
          showGivenAnswers={showGivenAnswers}
          onScoreRef={(playerId, element) => {
            if (element) {
              scoreRefs.current.set(playerId, element);
            } else {
              scoreRefs.current.delete(playerId);
            }
          }}
        />
      )}
    </div>
  );
});

StandingsSidebar.displayName = 'StandingsSidebar';
